import React, { useState } from "react";
import classNames from "classnames";

import {
  AutoDetectOption,
  NewParamSetType,
  ParamSetType,
  toParamSet,
  ValueTypeOptions,
  WorkbookParamInfo,
} from "../Workbook/util";

// Manages the values and NULL flags entered for a parameter set. The returned
// paramSet carries the workbook-wide parameter types alongside the entered
// values, for display, validation, and value conversion. Emptying a value
// removes its alias from the set so validation counts it as missing, unless
// the value is flagged as NULL or the type accepts an empty string.
export function useParamSetInputs(
  workbook: WorkbookParamInfo,
  initialParamSet: NewParamSetType | (() => NewParamSetType),
  onChange?: (newParamSet: NewParamSetType) => void,
) {
  const [newParamSet, setNewParamSetState] =
    useState<NewParamSetType>(initialParamSet);
  const paramSet = toParamSet(newParamSet, workbook);

  const setNewParamSet = (newSet: NewParamSetType) => {
    setNewParamSetState(newSet);
    onChange?.(newSet);
  };

  const handleParamValueChange = (alias: string, value: string) => {
    const prevParam = newParamSet[alias];
    const newSet = { ...newParamSet };
    const type = paramSet[alias]?.type;
    // An empty string is only a valid value for text and bytea types
    const allowsEmptyValue = type === "text" || type === "bytea";
    if (value === "" && !allowsEmptyValue && !prevParam?.isNull) {
      // remove from the set when the value becomes an empty string, so that
      // validation counts this parameter as missing
      delete newSet[alias];
    } else {
      newSet[alias] = {
        value: value,
        isNull: prevParam?.isNull,
      };
    }
    setNewParamSet(newSet);
  };

  const handleParamNullChange = (alias: string, isNull: boolean) => {
    const prevParam = newParamSet[alias];
    const newSet = { ...newParamSet };
    // when null is checked but there is an existing value, empty it
    newSet[alias] = {
      value: isNull ? undefined : prevParam?.value,
      isNull: isNull,
    };
    setNewParamSet(newSet);
  };

  return {
    paramSet,
    setNewParamSet,
    handleParamValueChange,
    handleParamNullChange,
  };
}

export const ParameterSetInputGrid = ({
  workbook,
  paramSet,
  onValueChange,
  onNullChange,
  id,
  className,
}: {
  workbook: WorkbookParamInfo;
  paramSet: ParamSetType;
  onValueChange: (alias: string, value: string) => void;
  onNullChange: (alias: string, isNull: boolean) => void;
  id?: string;
  className?: string;
}) => {
  const inputFields = workbook.parameterRefAliases.flatMap((alias) => {
    const aliasStr = `$${alias}`;
    const type =
      ValueTypeOptions.find(
        ({ value }) => value === paramSet[aliasStr]?.type,
      ) || AutoDetectOption;
    return [
      <div key={`label-${alias}`}>{aliasStr}</div>,
      <input
        key={`input-${alias}`}
        className="bg-white rounded border border-gray-300 box-content h-5 leading-5 px-2 py-1.5 disabled:bg-[#eee]"
        type="text"
        value={paramSet[aliasStr]?.value ?? ""}
        placeholder={type.value === "array" ? '{1,2,3} or {a,"b,",c}' : ""}
        disabled={paramSet[aliasStr]?.isNull}
        onChange={(e) => onValueChange(aliasStr, e.target.value)}
      />,
      <input
        key={`null-${alias}`}
        type="checkbox"
        checked={paramSet[aliasStr]?.isNull || false}
        onChange={(e) => onNullChange(aliasStr, e.target.checked)}
      />,
      <div key={`type-${alias}`}>{type.label}</div>,
    ];
  });

  return (
    <div
      id={id}
      className={classNames(
        "grid grid-cols-[min-content_1fr_40px_200px] gap-2 items-center font-medium",
        className,
      )}
    >
      <div>Name</div>
      <div>Value</div>
      <div>NULL</div>
      <div>Parameter Type</div>
      {inputFields}
    </div>
  );
};
